import React, { useState } from "react";
import { Input, Label, Form, FormFeedback, Button, Spinner } from "reactstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import { postResetPassword } from "../../helpers/fakebackend_helper";
import AhAuthShell from "./AhAuthShell";

const ResetPassword = () => {
    document.title = "Reset password | ApiHub";

    const { token } = useParams();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const validation = useFormik({
        initialValues: { password: "", confirmPassword: "" },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(6, "Password must be at least 6 characters")
                .required("Please enter your new password"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords do not match")
                .required("Please confirm your password"),
        }),
        onSubmit: async (values) => {
            setError(""); setSuccess("");
            if (!token) {
                setError("Reset token is missing from the URL.");
                return;
            }
            setSubmitting(true);
            try {
                const res = await postResetPassword({ token, password: values.password });
                if (res && res.success) {
                    setSuccess(res.message || "Password updated. Redirecting you to sign in…");
                    setTimeout(() => navigate("/login"), 2000);
                } else {
                    setError((res && res.message) || "Failed to reset password.");
                }
            } catch (err) {
                setError(typeof err === "string" ? err : (err && err.message) || "Network error.");
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <AhAuthShell
            eyebrow="ACCOUNT RECOVERY"
            title="Set a new password"
            subtitle="Choose a strong password you haven't used before."
            footer={<>Remembered your password? <Link to="/login">Sign in</Link></>}
        >
            {success && (
                <div className="ah-notice" role="status">
                    <div className="ah-notice-icon"><i className="ri-checkbox-circle-line"></i></div>
                    <div className="ah-notice-body">
                        <strong>Password updated.</strong>
                        <div style={{ marginTop: 4, color: "oklch(0.82 0.06 65)" }}>{success}</div>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: 20 }}>{error}</div>
            )}

            {!success && (
                <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                    <div className="mb-3">
                        <Label className="form-label" htmlFor="password">New password</Label>
                        <div className="position-relative auth-pass-inputgroup">
                            <Input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="At least 6 characters"
                                value={validation.values.password}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={validation.touched.password && !!validation.errors.password}
                                className="pe-5"
                            />
                            <button
                                type="button"
                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none"
                                onClick={() => setShowPassword(s => !s)}
                                tabIndex={-1}
                                aria-label="Toggle password visibility"
                            >
                                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                            </button>
                            {validation.touched.password && validation.errors.password && (
                                <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                            )}
                        </div>
                    </div>

                    <div className="mb-3">
                        <Label className="form-label" htmlFor="confirmPassword">Confirm password</Label>
                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            placeholder="Re-enter new password"
                            value={validation.values.confirmPassword}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                        />
                        {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                            <FormFeedback type="invalid">{validation.errors.confirmPassword}</FormFeedback>
                        )}
                    </div>

                    <Button color="primary" className="w-100 mt-2" type="submit" disabled={submitting}>
                        {submitting ? <><Spinner size="sm" className="me-2" />Updating…</> : "Reset password →"}
                    </Button>
                </Form>
            )}
        </AhAuthShell>
    );
};

export default ResetPassword;
